import {
  DefinedUseQueryResult,
  InfiniteData,
  useInfiniteQuery,
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import { useAuth } from "react-oidc-context";
import { User } from "oidc-client-ts";
// MUTATIONS
type AuthenticatedMutationFn<TVariables, TData> = (
  variables: TVariables,
  user: NonNullable<ReturnType<typeof useAuth>["user"]>,
) => Promise<TData>;

type AuthenticatedMutationOptions<TData, TError, TVariables, TContext> = Omit<
  UseMutationOptions<TData, TError, TVariables, TContext>,
  "mutationFn"
> & {
  // Keys (without the user-id prefix) to invalidate on success. Each is
  // prefixed with the current user id to match useAuthenticatedQuery keys.
  invalidateKeys?: unknown[][];
};

export function useAuthenticatedMutation<
  TData = unknown,
  TError = Error,
  TVariables = void,
  TContext = unknown,
>(
  mutationFn: AuthenticatedMutationFn<TVariables, TData>,
  options?: AuthenticatedMutationOptions<TData, TError, TVariables, TContext>,
): UseMutationResult<TData, TError, TVariables, TContext> {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { invalidateKeys, onSuccess, ...rest } = options ?? {};

  return useMutation({
    mutationFn: (variables: TVariables) => {
      if (!user) {
        throw new Error("No user found");
      }
      return mutationFn(variables, user);
    },
    onSuccess: async (data, variables, onMutateResult, context) => {
      if (invalidateKeys && user) {
        await Promise.all(
          invalidateKeys.map((key) =>
            queryClient.invalidateQueries({
              queryKey: [user.profile.sub, ...key],
            }),
          ),
        );
      }
      await onSuccess?.(data, variables, onMutateResult, context);
    },
    ...rest,
  });
}

// QUERY

type AuthenticatedQueryFn<TData> = (user: User) => Promise<TData>;

// Enhanced options that preserve initialData typing
type UseAuthenticatedQueryOptions<TData, TError> = Omit<
  UseQueryOptions<TData, TError>,
  "queryKey" | "queryFn"
>;

// Overloaded function signatures to handle initialData properly
export function useAuthenticatedQuery<TData = unknown, TError = Error>(
  queryKey: unknown[],
  queryFn: AuthenticatedQueryFn<TData>,
  options: UseAuthenticatedQueryOptions<TData, TError> & {
    initialData: TData | (() => TData);
  },
): DefinedUseQueryResult<TData, TError>;

export function useAuthenticatedQuery<TData = unknown, TError = Error>(
  queryKey: unknown[],
  queryFn: AuthenticatedQueryFn<TData>,
  options: UseAuthenticatedQueryOptions<TData, TError> & {
    initialData?: undefined;
  },
): UseQueryResult<TData | undefined, TError>;

export function useAuthenticatedQuery<TData = unknown, TError = Error>(
  queryKey: unknown[],
  queryFn: AuthenticatedQueryFn<TData>,
  options?: UseAuthenticatedQueryOptions<TData, TError>,
): UseQueryResult<TData | undefined, TError>;

export function useAuthenticatedQuery<TData = unknown, TError = Error>(
  queryKey: unknown[],
  queryFn: AuthenticatedQueryFn<TData>,
  options: UseAuthenticatedQueryOptions<TData, TError> = {},
): UseQueryResult<TData, TError> | DefinedUseQueryResult<TData, TError> {
  const { user } = useAuth();

  const authenticatedQueryKey = user
    ? [user.profile.sub, ...queryKey]
    : ["no-user", ...queryKey];

  return useQuery({
    queryKey: authenticatedQueryKey,
    queryFn: () => {
      if (!user) {
        throw new Error("User not authenticated");
      }
      return queryFn(user);
    },
    enabled:
      user !== null &&
      user !== undefined &&
      !user.expired &&
      (options.enabled ?? true),
    ...options,
  });
}

// INFINITE QUERY

export function useAuthenticatedInfiniteQuery<TData>(
  queryKey: unknown[],
  queryFn: (user: User, page: number) => Promise<TData>,
  getNextPageParam: (lastPage: TData) => number | undefined,
) {
  const { user } = useAuth();

  const authenticatedQueryKey = user
    ? [user.profile.sub, ...queryKey]
    : ["no-user", ...queryKey];

  return useInfiniteQuery<
    TData,
    Error,
    InfiniteData<TData, number>,
    unknown[],
    number
  >({
    queryKey: authenticatedQueryKey,
    queryFn: ({ pageParam }) => {
      if (!user) {
        throw new Error("User not authenticated");
      }
      return queryFn(user, pageParam);
    },
    initialPageParam: 0,
    getNextPageParam,
    enabled: user !== null && user !== undefined && !user.expired,
  });
}
